import styled from 'styled-components';

import rem from 'polished/lib/helpers/rem';
import toColorString from 'polished/lib/color/toColorString';

import color from '@decisiv/design-tokens/lib/color';
import spacing from '@decisiv/design-tokens/lib/spacing';

import Link from '@decisiv/ui-components/lib/components/Link';

// Real styled components rather than styled-components' `css` prop: portal's
// babel config has no babel-plugin-styled-components, so a `css={...}` would be
// dropped without warning. Same constraint the rest of this page works under.

/** A step's body: heading block, then the field, 20px apart as in the mockup. */
export const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base)};
`;

/** The heading row: title on the left, "Required Fields" opposite. */
export const StepHeading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${rem(spacing.base)};
`;

export const RequiredMarker = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 4)};
  flex-shrink: 0;
  white-space: nowrap;
`;

/**
 * The upload field. The gaps between its four parts are not uniform in the
 * design, so they are set per child rather than with one `gap` — measured off
 * Figma 787:76937, whose children sit at y=0 (label, 19 tall), y=29 (help), and
 * y=78 (the actions, 46 tall including the hint line beneath them).
 */
export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FieldHelp = styled.div`
  margin-top: ${rem(spacing.base / 2)};
`;

/** UPLOAD CSV FILE on the left, DOWNLOAD CSV TEMPLATE opposite (Figma 787:76937). */
export const UploadActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(spacing.base)};
  margin-top: ${rem(15)};
`;

/**
 * KeyDS Link underlines unconditionally — `sharedLinkStyles` sets
 * text-decoration on the element and again on :hover, with no prop to turn it
 * off. The design draws DOWNLOAD CSV TEMPLATE as plain blue text with an icon,
 * so the rule is overridden here. Link still owns everything else about it,
 * including the anchor semantics a download needs.
 */
export const TemplateLink = styled(Link)`
  &,
  &:hover {
    text-decoration: none;
  }
`;

/**
 * The chosen file, drawn as the mockup's bordered row: name above size, with the
 * remove control at the right edge. Turns amber once the file is rejected, which
 * is how the design distinguishes 1.2.1/1.2.2/1.2.3 from the accepted state —
 * amber rather than red, which is what all three frames draw.
 *
 * The tokens are RGB objects, not strings, so they need toColorString the way
 * the sibling CreateUserWizard/styles.js does it. Naming one that does not exist
 * yields `border: 1px solid undefined`, which the browser drops silently: the
 * box then has no border at all and there is nothing on screen to say why.
 */
export const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(spacing.base / 2)};
  margin-top: ${rem(15)};
  padding: ${rem(spacing.base / 2)};
  border: 1px solid
    ${({ $invalid }) =>
      toColorString(
        $invalid ? color.status.warning.medium : color.opacity.charcoal15,
      )};
  border-radius: ${rem(4)};
`;

/**
 * An icon paired with a line of small text — the mockup's "?" hint under the
 * upload actions, and the "!" it replaces that hint with when a file is
 * rejected. Baseline-ish alignment rather than centered, so a message that wraps
 * to two lines keeps its icon against the first.
 */
export const HintRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(spacing.base / 4)};
  margin-top: ${rem(spacing.base / 4)};

  svg {
    flex-shrink: 0;
    margin-top: ${rem(1)};
  }
`;

/** The native input is the only way to open a file picker; the Button fronts it. */
export const HiddenFileInput = styled.input`
  display: none;
`;

/** The processing panel: spinner beside the two lines of copy (Figma 787:76954). */
export const ProcessingRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(spacing.base / 2)};
`;

/**
 * The summary body: the message on the left, DOWNLOAD REPORT opposite.
 *
 * DOWNLOAD REPORT is a Button rendered `as="a"`, because only a real anchor
 * honours `download`. That brings the browser's default underline for anchor
 * text with it, which the outlined button in the design does not have. The rule
 * lives here rather than on a `styled(Button)` wrapper: styled-components treats
 * `as` as its own prop and would swap the wrapper for a bare <a>, dropping
 * Button's rendering entirely.
 */
export const SummaryRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${rem(spacing.base)};

  a,
  a:hover {
    text-decoration: none;
  }
`;

export const SummaryLines = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * The wizard's footer actions.
 *
 * Bare Buttons in `renderActions` end up at opposite ends of the footer, because
 * the footer is a space-between row; wrapping them keeps the pair together at
 * the right, as the mockup draws them.
 */
export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 2)};
  margin-left: auto;
`;
